import React from "react";
import * as Sentry from "@sentry/react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Layout } from "./components/Layout";
import { PatientLayout } from "./components/PatientLayout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Patients from "./pages/Patients";
import Records from "./pages/Records";
import ClinicalRecord from "./pages/ClinicalRecord";
import RescheduleQueue from "./pages/RescheduleQueue";
import AvailabilityBlocks from "./pages/AvailabilityBlocks";
import Reports from "./pages/Reports";
import Users from "./pages/Users";
import Services from "./pages/Services";
import Products from "./pages/Products";
import Sales from "./pages/Sales";
import SaleDetail from "./pages/SaleDetail";
import Settings from "./pages/Settings";
import PublicBooking from "./pages/PublicBooking";
import PatientPortal from "./pages/PatientPortal";
import PatientDashboard from "./pages/patient/PatientDashboard";
import PatientAppointmentsList from "./pages/patient/PatientAppointmentsList";
import PatientAppointmentShow from "./pages/patient/PatientAppointmentShow";
import PatientProfile from "./pages/patient/PatientProfile";

const PrivateRoute = ({ children }) => {
  const { user, loading, isPatient } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#335C67] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#5D5C61]">Cargando...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirigir usuarios PACIENTE a su ruta específica
  if (isPatient) {
    return <Navigate to="/mi-perfil" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

const PatientLayoutRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#335C67] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#5D5C61]">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'PACIENTE') {
    return <Navigate to="/" replace />;
  }

  return <PatientLayout>{children}</PatientLayout>;
};

const AdminRoute = ({ children }) => {
  const { user, isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#335C67] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#5D5C61]">Cargando...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

function AppRoutes() {
  const { user, loading } = useAuth();
  
  return (
    <Routes>
      {/* PUBLIC ROUTES - No authentication required */}
      <Route path="/agendar" element={<PublicBooking />} />
      <Route path="/portal" element={<PatientPortal />} />
      
      {/* AUTH ROUTES */}
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      
      {/* PRIVATE ROUTES - Require authentication (ADMIN/ASISTENTE) */}
      <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      <Route path="/clientes" element={<PrivateRoute><Patients /></PrivateRoute>} />
      <Route path="/pacientes" element={<PrivateRoute><Patients /></PrivateRoute>} />
      <Route path="/expedientes" element={<PrivateRoute><Records /></PrivateRoute>} />
      <Route path="/expedientes/:patientId" element={<PrivateRoute><ClinicalRecord /></PrivateRoute>} />
      <Route path="/reagendar" element={<PrivateRoute><RescheduleQueue /></PrivateRoute>} />
      <Route path="/bloqueos" element={<PrivateRoute><AvailabilityBlocks /></PrivateRoute>} />
      <Route path="/reportes" element={<PrivateRoute><Reports /></PrivateRoute>} />
      
      {/* ADMIN ROUTES */}
      <Route path="/usuarios" element={<AdminRoute><Users /></AdminRoute>} />
      <Route path="/servicios" element={<AdminRoute><Services /></AdminRoute>} />
      <Route path="/productos" element={<AdminRoute><Products /></AdminRoute>} />
      <Route path="/ventas" element={<AdminRoute><Sales /></AdminRoute>} />
      <Route path="/ventas/:id" element={<AdminRoute><SaleDetail /></AdminRoute>} />
      <Route path="/configuracion" element={<AdminRoute><Settings /></AdminRoute>} />
      
      {/* CLIENT ROUTES - Portal del cliente (layout y vistas propias) */}
      <Route path="/mi-perfil" element={<PatientLayoutRoute><PatientDashboard /></PatientLayoutRoute>} />
      <Route path="/mis-citas" element={<PatientLayoutRoute><PatientAppointmentsList /></PatientLayoutRoute>} />
      <Route path="/mis-citas/:id" element={<PatientLayoutRoute><PatientAppointmentShow /></PatientLayoutRoute>} />
      <Route path="/mi-cuenta" element={<PatientLayoutRoute><PatientProfile /></PatientLayoutRoute>} />

      {/* FALLBACK */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function sentryErrorFallback({ error, resetError }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#FDFCF8] px-6 text-center">
      <h1 className="text-xl font-semibold text-[#335C67]">Algo salió mal</h1>
      <p className="max-w-md text-sm text-[#5D5C61]">
        Se ha registrado el error para revisión. Puedes reintentar o recargar la página.
      </p>
      <div className="flex flex-wrap justify-center gap-2">
        <button
          type="button"
          className="rounded-md bg-[#335C67] px-4 py-2 text-sm font-medium text-white"
          onClick={resetError}
        >
          Reintentar
        </button>
        <button
          type="button"
          className="rounded-md border border-[#335C67] px-4 py-2 text-sm font-medium text-[#335C67]"
          onClick={() => window.location.reload()}
        >
          Recargar
        </button>
      </div>
      {process.env.NODE_ENV === "development" && error instanceof Error ? (
        <pre className="mt-4 max-h-40 max-w-full overflow-auto rounded bg-neutral-100 p-3 text-left text-xs text-neutral-800">
          {error.message}
        </pre>
      ) : null}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster 
          position="top-right"
          richColors
          closeButton
          duration={3000}
          toastOptions={{
            style: {
              fontFamily: 'Public Sans, sans-serif',
            },
          }}
        />
        <Sentry.ErrorBoundary
          fallback={sentryErrorFallback}
          beforeCapture={(scope, error, componentStack) => {
            scope.setContext("react_ui", {
              path:
                typeof window !== "undefined"
                  ? `${window.location.pathname}${window.location.search}`
                  : "",
              userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
              environment: process.env.NODE_ENV,
            });
            scope.setExtra("componentStack", componentStack);
          }}
        >
          <AppRoutes />
        </Sentry.ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
